import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import axios from "axios";
import { BASE_URL } from "../api";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";
import CommonTable from "../component/CommonTable";

export default function CpaHistory() {
    const handleError = useHandleError();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const fetchCpaHistory = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            try {
                // Try GET /ib/cpa_history
                res = await axios.get(`${BASE_URL}/ib/cpa_history`, {
                    headers: getApiHeaders()
                });
            } catch (err) {
                if (err.response?.status === 404) {
                    res = await axios.get(`${BASE_URL}/IB/cpa_history`, {
                        headers: getApiHeaders()
                    });
                } else if (err.response?.status === 405) {
                    res = await axios.post(`${BASE_URL}/ib/cpa_history`, {}, {
                        headers: getApiHeaders()
                    });
                } else {
                    throw err;
                }
            }

            if (res && res.data) {
                let resData = res.data.data;
                if (typeof resData === "string") {
                    try {
                        resData = JSON.parse(resData);
                    } catch {
                        // ignore
                    }
                }

                if (Array.isArray(resData)) {
                    setData(resData);
                } else if (resData && typeof resData === "object") {
                    const possibleList =
                        resData.history ||
                        resData.cpa_history ||
                        resData.records ||
                        resData.list ||
                        resData.data;

                    if (Array.isArray(possibleList)) {
                        setData(possibleList);
                    } else {
                        setData([]);
                    }
                } else if (Array.isArray(res.data)) {
                    setData(res.data);
                } else {
                    setData([]);
                }

                if (
                    res.data.code !== 200 &&
                    res.data.code !== "200" &&
                    res.data.status !== "success" &&
                    res.data.status !== true
                ) {
                    if (res.data.message) {
                        MessageToster("error", res.data.message);
                    }
                }
            } else {
                setData([]);
            }
        } catch (error) {
            setData([]);
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        fetchCpaHistory();
    }, [fetchCpaHistory]);

    const clearFilters = () => {
        setFromDate("");
        setToDate("");
        setSearchTerm("");
    };

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const rawDate =
                item.ondate ||
                item.created_at ||
                item.create_date ||
                item.date ||
                item.time;

            if (fromDate) {
                const itemDate = new Date(rawDate);
                const from = new Date(fromDate);
                from.setHours(0, 0, 0, 0);
                if (!isNaN(itemDate.getTime()) && itemDate < from) return false;
            }

            if (toDate) {
                const itemDate = new Date(rawDate);
                const to = new Date(toDate);
                to.setHours(23, 59, 59, 999);
                if (!isNaN(itemDate.getTime()) && itemDate > to) return false;
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                return Object.values(item).some(
                    (val) => val != null && String(val).toLowerCase().includes(term)
                );
            }

            return true;
        });
    }, [data, fromDate, toDate, searchTerm]);

    const handleExport = () => {
        if (!filteredData || filteredData.length === 0) return;
        const exportRows = filteredData.map((row, idx) => ({
            "No.": idx + 1,
            "Date": formatDateDDMMYYYY(row.ondate || row.created_at || row.date),
            "Username": row.username || "-",
            // "IB Email": row.ib_email || row.ib || "-",
            "CPA Amount": row.cpa_amount != null ? `$${Number(row.cpa_amount).toFixed(2)}` : "-",
            "Status": row.status || "-"
        }));
        exportToExcel(exportRows, "IB_CPA_History_Report", "default");
    };

    const columns = [
        {
            header: "Date",
            render: (row) => {
                const raw = row.ondate || row.created_at || row.create_date || row.date || row.time;
                return raw ? formatDateDDMMYYYY(raw) : "-";
            }
        },
        {
            header: "Username",
            render: (row) => {
                return (
                    <span className="fw-semibold text-dark">
                        {row.username || "-"}
                    </span>
                );
            }
        },
        // {
        //     header: "IB Email",
        //     render: (row) => {
        //         return (
        //             <span className="text-secondary font-monospace">
        //                 {row.ib_email || row.ib || "-"}
        //             </span>
        //         );
        //     }
        // },
        {
            header: "CPA Amount",
            render: (row) => {
                const val = row.cpa_amount ?? row.amount;
                if (val == null || val === "") return "-";
                const num = Number(val);
                if (isNaN(num)) return String(val);
                return (
                    <span className="fw-bold text-success font-monospace font-size-14">
                        ${num.toFixed(2)}
                    </span>
                );
            }
        },
        {
            header: "Status",
            render: (row) => {
                const status = row.status || "Confirm";
                const st = String(status).toLowerCase();
                let badgeClass = "bg-secondary bg-opacity-10 text-secondary";
                if (["confirm", "confirmed", "approved", "approve", "paid", "success"].includes(st)) {
                    badgeClass = "bg-success bg-opacity-10 text-success";
                } else if (["pending", "in review", "processing"].includes(st)) {
                    badgeClass = "bg-warning bg-opacity-10 text-warning";
                } else if (["rejected", "reject", "failed", "cancelled"].includes(st)) {
                    badgeClass = "bg-danger bg-opacity-10 text-danger";
                }
                return (
                    <span className={`badge px-2 py-1 ${badgeClass}`}>
                        {status}
                    </span>
                );
            }
        }
    ];

    return (
        <div id="layout-wrapper">
            {loading && <Loader />}
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h2 className="fw-700 mb-sm-0">IB CPA History</h2>
                                </div>
                            </div>
                        </div>

                        {/* CPA Records Table */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                                            <div>
                                                <label className="form-label mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fromDate}
                                                    max={toDate || today}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={toDate}
                                                    min={fromDate || undefined}
                                                    max={today}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label mb-1">Search</label>
                                                <input
                                                    type="search"
                                                    className="form-control form-control-sm"
                                                    placeholder="Search username, email..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-secondary waves-effect"
                                                    onClick={clearFilters}
                                                    disabled={!fromDate && !toDate && !searchTerm}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary waves-effect"
                                                    onClick={fetchCpaHistory}
                                                    disabled={loading}
                                                    title="Refresh Data"
                                                >
                                                    <i className={`bx bx-refresh me-1 ${loading ? "bx-spin" : ""}`}></i>
                                                    Refresh
                                                </button>
                                            </div>
                                            <div className="ms-auto">
                                                <ExportButton
                                                    onClick={handleExport}
                                                    disabled={!filteredData || filteredData.length === 0}
                                                />
                                            </div>
                                        </div>

                                        <CommonTable
                                            columns={columns}
                                            data={filteredData}
                                            loading={loading}
                                            noDataMessage="No CPA history records found"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}
